import { useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Bell, Trash2, ChevronRight } from "lucide-react";
import { EmailPrefs } from "../../components/app/EmailPrefs";
import { toast } from "sonner";
import { useApi } from "../../hooks/useApi";
import { web, errMsg } from "../../lib/api";
import { PageHeader, Loader, ErrorNote, Empty, Btn, shortDate, positionsOf } from "../../components/app/kit";

const searchLink = (a) => `/recruiter/search?${new URLSearchParams(Object.entries({ archetype: a.archetype, gy: a.grad_year, pos: a.position }).filter(([, v]) => v)).toString()}`;

export default function Alerts() {
  const { data, loading, error, reload } = useApi("/recruiter/alerts", web);
  const list = Array.isArray(data) ? data : [];
  const remove = async (a) => {
    if (!window.confirm(`Delete the "${a.label}" alert?`)) return;
    try { await web.delete(`/recruiter/alerts/${a.id}`); toast.success("Alert deleted"); reload(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div>
      <PageHeader eyebrow="Saved style searches" title="Alerts">
        <Link to="/recruiter/search" data-testid="new-alert-link" className="inline-flex items-center gap-2 rounded-full bg-[#39FF14] px-5 py-2.5 text-sm font-semibold text-[#0A0E1A] hover:bg-[#30D911]"><Bell className="h-4 w-4" />New alert</Link>
      </PageHeader>
      <EmailPrefs />
      {loading && !data ? <Loader /> : error ? <ErrorNote error={error} /> : !list.length ? (
        <Empty title="No alerts yet" text="On Search, pick a playing style, add a class year or position if you like, and tap Save alert. You'll see new matches here." />
      ) : (
        <ul className="grid gap-3 md:grid-cols-2">
          {list.map((a) => (
            <li key={a.id} data-testid={`alert-${a.id}`} className="flex items-center gap-3 rounded-2xl border border-[#1E293B] bg-[#0F172A] p-4 transition-colors hover:border-[#39FF14]/50">
              <Link to={`/recruiter/alerts/${a.id}`} data-testid={`alert-open-${a.id}`} className="flex min-w-0 flex-1 items-center gap-3">
                <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${a.new_count ? "bg-[#39FF14] text-[#0A0E1A]" : "bg-[#162032] text-slate-400"}`}><Bell className="h-5 w-5" /></span>
                <span className="min-w-0">
                  <span className="block truncate font-display text-xl font-bold uppercase text-white">{a.label}</span>
                  <span className="font-mono-gd text-xs uppercase tracking-wider text-slate-400">
                    {a.new_count ? <span data-testid={`alert-new-${a.id}`} className="text-[#39FF14]">{a.new_count} new · </span> : null}{a.total} matching · saved {shortDate(a.created_at)}
                  </span>
                </span>
                <ChevronRight className="ml-auto h-5 w-5 shrink-0 text-slate-500" />
              </Link>
              <button data-testid={`alert-delete-${a.id}`} onClick={() => remove(a)} className="shrink-0 rounded-full p-2 text-slate-400 hover:text-red-300" aria-label="Delete alert"><Trash2 className="h-4 w-4" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export function AlertDetail() {
  const { alertId } = useParams();
  const navigate = useNavigate();
  const { data, loading, error } = useApi(`/recruiter/alerts/${alertId}`, web);
  const ready = !!data;
  useEffect(() => { if (ready) web.post(`/recruiter/alerts/${alertId}/seen`).then(() => window.dispatchEvent(new Event("gd-alerts"))).catch(() => {}); }, [ready, alertId]);
  const a = data?.alert;
  const rows = Array.isArray(data?.matches) ? data.matches : [];
  if (loading && !data) return <Loader />;
  if (error) return <ErrorNote error={error} />;
  return (
    <div>
      <Link to="/recruiter/alerts" data-testid="back-to-alerts" className="mb-6 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-[#39FF14]"><ArrowLeft className="h-4 w-4" />Alerts</Link>
      <PageHeader eyebrow={`${a?.total ?? 0} matching${a?.new_count ? ` · ${a.new_count} new` : ""}`} title={a?.label ?? "Alert"}>
        <Btn variant="ghost" data-testid="alert-open-search" onClick={() => navigate(searchLink(a ?? {}))}>Open in search</Btn>
      </PageHeader>
      {!rows.length ? <Empty title="No matches yet" text="We'll flag players here as soon as a coach tags one with this style." /> : (
        <ul className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((p) => (
            <li key={p.id} data-testid={`alert-match-${p.id}`} className={`min-w-0 rounded-2xl border p-4 ${p.is_new ? "border-[#39FF14]/60 bg-[#39FF14]/5" : "border-[#1E293B] bg-[#0F172A]"}`}>
              <div className="flex items-start justify-between gap-2">
                <p className="min-w-0 truncate font-display text-xl font-bold uppercase text-white">{p.name}</p>
                {p.is_new && <span data-testid={`alert-match-new-${p.id}`} className="shrink-0 rounded-full bg-[#39FF14] px-2.5 py-1 font-mono-gd text-[10px] font-bold uppercase text-[#0A0E1A]">New</span>}
              </div>
              <p className="truncate font-mono-gd text-xs uppercase tracking-wider text-slate-400">{positionsOf(p)} · {p.grad_year ?? "—"} · {p.team_name}</p>
              <p className="mt-2 truncate text-xs text-slate-300">{[p.primary_archetype, p.secondary_archetype].filter(Boolean).join(" / ")}</p>
              {p.saved && <Link to={`/recruiter/prospects/${p.id}`} className="mt-2 inline-block text-xs font-semibold text-[#39FF14] hover:underline">Open prospect</Link>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
